import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Edit2, Send, ArrowLeft } from 'lucide-react';
import { SignaturePad } from '../common/SignaturePad';

interface TMTicketPreviewProps {
  jobName: string;
  jobNumber: string;
  submittedBy: string;
  date: Date;
  superintendentName: string;
  superintendentEmail: string;
  employees: Array<{ name: string; role: string; hours: number }>;
  equipment: Array<{
    name: string;
    type: string;
    hours: number;
    isMobilized: boolean;
    isRental: boolean;
    rentalCompany?: string;
  }>;
  materials: Array<{
    type: 'import' | 'export';
    description: string;
    quantity: number;
    unit: string;
  }>;
  dumpsters?: Array<{
    dumpster_type: string;
    size: string;
    quantity: number;
  }>;
  notes: string;
  totalEmployeeHours: number;
  totalEquipmentHours: number;
  language: 'en' | 'es';
  onEdit: () => void;
  onSign: (signature: string) => void;
  // New T&M enhancement fields
  workDescription?: string;
  gcName?: string;
  startTime?: Date;
  stopTime?: Date;
}

const translations = {
  en: {
    timeAndMaterials: 'TIME & MATERIALS TICKET',
    ticketNumber: 'TICKET NUMBER',
    dateSubmitted: 'DATE SUBMITTED',
    jobSite: 'JOB SITE',
    submittedBy: 'SUBMITTED BY',
    superintendent: 'SUPERINTENDENT',
    employeeHours: 'EMPLOYEE HOURS',
    equipmentHours: 'EQUIPMENT HOURS',
    employees: 'EMPLOYEES',
    name: 'NAME',
    role: 'ROLE',
    hours: 'HOURS',
    equipment: 'EQUIPMENT',
    type: 'TYPE',
    mobilized: 'MOBILIZED',
    rental: 'RENTAL',
    materials: 'MATERIALS',
    imports: 'IMPORTS',
    exports: 'EXPORTS',
    dumpsters: 'DUMPSTERS',
    notes: 'NOTES',
    superintendentSignature: 'SUPERINTENDENT SIGNATURE',
    authorizedSignature: 'Authorized Signature',
    date: 'Date',
    editTicket: 'Edit Ticket',
    submitSignedTicket: 'Submit Signed Ticket',
    signatureRequired: 'Please sign above to submit',
    toBeAssigned: 'To be assigned',
    workDescription: 'SCOPE OF WORK',
    generalContractor: 'GENERAL CONTRACTOR',
    startTime: 'START TIME',
    stopTime: 'STOP TIME'
  },
  es: {
    timeAndMaterials: 'BOLETO DE TIEMPO Y MATERIALES',
    ticketNumber: 'NUMERO DE BOLETO',
    dateSubmitted: 'FECHA DE ENVIO',
    jobSite: 'SITIO DE TRABAJO',
    submittedBy: 'ENVIADO POR',
    superintendent: 'SUPERINTENDENTE',
    employeeHours: 'HORAS DE EMPLEADOS',
    equipmentHours: 'HORAS DE EQUIPO',
    employees: 'EMPLEADOS',
    name: 'NOMBRE',
    role: 'ROL',
    hours: 'HORAS',
    equipment: 'EQUIPO',
    type: 'TIPO',
    mobilized: 'MOVILIZADO',
    rental: 'ALQUILER',
    materials: 'MATERIALES',
    imports: 'IMPORTACIONES',
    exports: 'EXPORTACIONES',
    dumpsters: 'CONTENEDORES',
    notes: 'NOTAS',
    superintendentSignature: 'FIRMA DEL SUPERINTENDENTE',
    authorizedSignature: 'Firma Autorizada',
    date: 'Fecha',
    editTicket: 'Editar Boleto',
    submitSignedTicket: 'Enviar Boleto Firmado',
    signatureRequired: 'Por favor firme arriba para enviar',
    toBeAssigned: 'Por asignar',
    workDescription: 'ALCANCE DEL TRABAJO',
    generalContractor: 'CONTRATISTA GENERAL',
    startTime: 'HORA DE INICIO',
    stopTime: 'HORA DE FIN'
  }
};

// Format role for display
const formatRole = (role: string): string => {
  return role
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const TMTicketPreview: React.FC<TMTicketPreviewProps> = ({
  jobName,
  jobNumber,
  submittedBy,
  date,
  superintendentName,
  superintendentEmail,
  employees,
  equipment,
  materials,
  dumpsters = [],
  notes,
  totalEmployeeHours,
  totalEquipmentHours,
  language,
  onEdit,
  onSign,
  workDescription,
  gcName,
  startTime,
  stopTime
}) => {
  const t = translations[language];
  const [signatureBase64, setSignatureBase64] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);

  // Scroll to top when preview opens - use multiple methods for reliability
  useEffect(() => {
    // Immediate scroll
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;

    // Delayed scroll with ref for mobile reliability
    const timer = setTimeout(() => {
      if (topRef.current) {
        topRef.current.scrollIntoView({ behavior: 'instant', block: 'start' });
      }
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, 50);

    return () => clearTimeout(timer);
  }, []);

  // Format date for display
  const formattedDate = useMemo(() => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }, [date]);

  // Separate imports and exports
  const importMaterials = useMemo(() =>
    materials.filter(m => m.type === 'import'), [materials]);
  const exportMaterials = useMemo(() =>
    materials.filter(m => m.type === 'export'), [materials]);

  const handleSubmit = async () => {
    if (!signatureBase64) return;
    setIsSubmitting(true);
    try {
      await onSign(signatureBase64);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div ref={topRef} style={{
      backgroundColor: '#f3f4f6',
      minHeight: '100vh',
      padding: '16px'
    }}>
      {/* Preview Container - Looks like a paper document */}
      <div style={{
        maxWidth: '600px',
        margin: '0 auto',
        backgroundColor: 'white',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        borderRadius: '8px',
        overflow: 'hidden'
      }}>
        {/* Black Header */}
        <div style={{
          backgroundColor: '#000000',
          color: 'white',
          padding: '20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{
            fontSize: '24px',
            fontWeight: '700',
            letterSpacing: '1px'
          }}>
            BRADANNA
          </div>
          <div style={{
            textAlign: 'right',
            fontSize: '12px',
            lineHeight: '1.5'
          }}>
            <div>4949 Tamiami Trail N, Suite 208</div>
            <div>Naples, FL 34103</div>
            <div>239-455-8891</div>
          </div>
        </div>

        {/* Red Title Bar */}
        <div style={{
          backgroundColor: '#dc2626',
          color: 'white',
          padding: '12px 20px',
          fontSize: '16px',
          fontWeight: '700',
          letterSpacing: '1px',
          textAlign: 'center'
        }}>
          {t.timeAndMaterials}
        </div>

        {/* Ticket Info Row */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <div style={{ padding: '12px 20px', borderRight: '1px solid #e5e7eb' }}>
            <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', marginBottom: '4px' }}>
              {t.ticketNumber}
            </div>
            <div style={{ fontSize: '16px', fontWeight: '600', color: '#374151' }}>
              {t.toBeAssigned}
            </div>
          </div>
          <div style={{ padding: '12px 20px' }}>
            <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', marginBottom: '4px' }}>
              {t.dateSubmitted}
            </div>
            <div style={{ fontSize: '16px', fontWeight: '600', color: '#374151' }}>
              {formattedDate}
            </div>
          </div>
        </div>

        {/* Job Site & Submitted By Row */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <div style={{ padding: '12px 20px', borderRight: '1px solid #e5e7eb' }}>
            <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', marginBottom: '4px' }}>
              {t.jobSite}
            </div>
            <div style={{ fontSize: '15px', fontWeight: '600', color: '#1f2937' }}>
              {jobName}
            </div>
            {jobNumber && (
              <div style={{ fontSize: '13px', color: '#6b7280' }}>
                #{jobNumber}
              </div>
            )}
          </div>
          <div style={{ padding: '12px 20px' }}>
            <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', marginBottom: '4px' }}>
              {t.submittedBy}
            </div>
            <div style={{ fontSize: '15px', fontWeight: '600', color: '#1f2937' }}>
              {submittedBy}
            </div>
          </div>
        </div>

        {/* GC & Superintendent Row - GC on left, Superintendent on right (matches PDF) */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: gcName ? '1fr 1fr' : '1fr',
          borderBottom: '1px solid #e5e7eb'
        }}>
          {gcName && (
            <div style={{ padding: '12px 20px', borderRight: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', marginBottom: '4px' }}>
                {t.generalContractor}
              </div>
              <div style={{ fontSize: '15px', fontWeight: '600', color: '#166534' }}>
                {gcName}
              </div>
            </div>
          )}
          <div style={{ padding: '12px 20px' }}>
            <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', marginBottom: '4px' }}>
              {t.superintendent}
            </div>
            <div style={{ fontSize: '15px', fontWeight: '600', color: '#1f2937' }}>
              {superintendentName}
            </div>
            <div style={{ fontSize: '13px', color: '#6b7280' }}>
              {superintendentEmail}
            </div>
          </div>
        </div>

        {/* Start/Stop Times */}
        {(startTime || stopTime) && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            borderBottom: '1px solid #e5e7eb'
          }}>
            <div style={{ padding: '12px 20px', borderRight: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', marginBottom: '4px' }}>
                {t.startTime}
              </div>
              <div style={{ fontSize: '16px', fontWeight: '600', color: '#374151' }}>
                {startTime ? new Date(startTime).toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true
                }) : '--'}
              </div>
            </div>
            <div style={{ padding: '12px 20px' }}>
              <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', marginBottom: '4px' }}>
                {t.stopTime}
              </div>
              <div style={{ fontSize: '16px', fontWeight: '600', color: '#374151' }}>
                {stopTime ? new Date(stopTime).toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true
                }) : '--'}
              </div>
            </div>
          </div>
        )}

        {/* Work Description / Scope of Work (matches PDF layout) */}
        {workDescription && (
          <div style={{
            padding: '12px 20px',
            borderBottom: '1px solid #e5e7eb',
            backgroundColor: '#f9fafb'
          }}>
            <div style={{ fontSize: '11px', color: '#000', fontWeight: '600', marginBottom: '8px' }}>
              {t.workDescription}
            </div>
            <div style={{
              fontSize: '14px',
              color: '#000',
              lineHeight: '1.5',
              whiteSpace: 'pre-wrap'
            }}>
              {workDescription}
            </div>
          </div>
        )}

        {/* Hours Summary Boxes - Muted grey style */}
        <div style={{
          display: 'flex',
          padding: '16px 20px',
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: 'rgba(0,0,0,0.02)',
          gap: '40px'
        }}>
          {/* Employee Hours */}
          <div>
            <div style={{ fontSize: '11px', color: '#374151', fontWeight: '600', marginBottom: '4px' }}>
              {t.employeeHours}
            </div>
            <div style={{ fontSize: '18px', fontWeight: '700', color: '#374151' }}>
              {totalEmployeeHours.toFixed(1)}
            </div>
          </div>

          {/* Equipment Hours */}
          <div>
            <div style={{ fontSize: '11px', color: '#374151', fontWeight: '600', marginBottom: '4px' }}>
              {t.equipmentHours}
            </div>
            <div style={{ fontSize: '18px', fontWeight: '700', color: '#374151' }}>
              {totalEquipmentHours.toFixed(1)}
            </div>
          </div>
        </div>

        {/* Employees Table */}
        {employees.length > 0 && (
          <div style={{ borderBottom: '1px solid #e5e7eb' }}>
            <div style={{
              padding: '12px 20px',
              backgroundColor: '#f9fafb',
              borderBottom: '1px solid #e5e7eb',
              fontSize: '13px',
              fontWeight: '700',
              color: '#374151'
            }}>
              {t.employees} ({employees.length})
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f3f4f6' }}>
                  <th style={{ padding: '10px 20px', textAlign: 'left', fontSize: '11px', color: '#6b7280', fontWeight: '600' }}>
                    {t.name}
                  </th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '11px', color: '#6b7280', fontWeight: '600' }}>
                    {t.role}
                  </th>
                  <th style={{ padding: '10px 20px', textAlign: 'right', fontSize: '11px', color: '#6b7280', fontWeight: '600' }}>
                    {t.hours}
                  </th>
                </tr>
              </thead>
              <tbody>
                {employees.map((emp, index) => (
                  <tr key={index} style={{ backgroundColor: index % 2 === 1 ? '#f9fafb' : 'white' }}>
                    <td style={{ padding: '10px 20px', fontSize: '14px', color: '#1f2937', fontWeight: '500' }}>
                      {emp.name}
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: '14px', color: '#6b7280' }}>
                      {formatRole(emp.role)}
                    </td>
                    <td style={{
                      padding: '10px 20px',
                      textAlign: 'right',
                      fontSize: '14px',
                      color: '#22c55e',
                      fontWeight: '600'
                    }}>
                      {emp.hours.toFixed(1)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Equipment Table */}
        {equipment.length > 0 && (
          <div style={{ borderBottom: '1px solid #e5e7eb' }}>
            <div style={{
              padding: '12px 20px',
              backgroundColor: '#f9fafb',
              borderBottom: '1px solid #e5e7eb',
              fontSize: '13px',
              fontWeight: '700',
              color: '#374151'
            }}>
              {t.equipment} ({equipment.length})
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f3f4f6' }}>
                  <th style={{ padding: '10px 20px', textAlign: 'left', fontSize: '11px', color: '#6b7280', fontWeight: '600' }}>
                    {t.equipment}
                  </th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '11px', color: '#6b7280', fontWeight: '600' }}>
                    {t.type}
                  </th>
                  <th style={{ padding: '10px 20px', textAlign: 'right', fontSize: '11px', color: '#6b7280', fontWeight: '600' }}>
                    {t.hours}
                  </th>
                </tr>
              </thead>
              <tbody>
                {equipment.map((eq, index) => (
                  <React.Fragment key={index}>
                    <tr style={{ backgroundColor: index % 2 === 1 ? '#f9fafb' : 'white' }}>
                      <td style={{ padding: '10px 20px', fontSize: '14px', color: '#1f2937', fontWeight: '500' }}>
                        {eq.name}
                        {eq.isRental && (
                          <span style={{
                            marginLeft: '8px',
                            backgroundColor: '#f59e0b',
                            color: 'white',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '10px',
                            fontWeight: '600'
                          }}>
                            {t.rental}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: '14px', color: '#6b7280' }}>
                        {eq.type}
                      </td>
                      <td style={{
                        padding: '10px 20px',
                        textAlign: 'right',
                        fontSize: '14px',
                        color: '#f59e0b',
                        fontWeight: '600'
                      }}>
                        {eq.hours.toFixed(1)}
                      </td>
                    </tr>
                    {(eq.isMobilized || eq.isRental) && (
                      <tr style={{ backgroundColor: index % 2 === 1 ? '#f9fafb' : 'white' }}>
                        <td colSpan={3} style={{ padding: '0 20px 10px 20px', fontSize: '12px', color: '#6b7280' }}>
                          {eq.isMobilized && (
                            <span style={{
                              backgroundColor: '#dbeafe',
                              color: '#1e40af',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              marginRight: '8px'
                            }}>
                              {t.mobilized}
                            </span>
                          )}
                          {eq.isRental && eq.rentalCompany && (
                            <span>{eq.rentalCompany}</span>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Materials Section */}
        {(materials.length > 0 || dumpsters.length > 0) && (
          <div style={{ borderBottom: '1px solid #e5e7eb' }}>
            <div style={{
              padding: '12px 20px',
              backgroundColor: '#f9fafb',
              borderBottom: '1px solid #e5e7eb',
              fontSize: '13px',
              fontWeight: '700',
              color: '#374151'
            }}>
              {t.materials}
            </div>
            <div style={{ padding: '16px 20px' }}>
              {/* Imports */}
              {importMaterials.length > 0 && (
                <div style={{ marginBottom: importMaterials.length > 0 && exportMaterials.length > 0 ? '16px' : '0' }}>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#166534',
                    marginBottom: '8px',
                    textTransform: 'uppercase'
                  }}>
                    {t.imports}:
                  </div>
                  {importMaterials.map((mat, index) => (
                    <div key={index} style={{
                      padding: '8px 12px',
                      backgroundColor: '#f0fdf4',
                      borderRadius: '6px',
                      marginBottom: '6px',
                      fontSize: '14px',
                      color: '#166534',
                      display: 'flex',
                      justifyContent: 'space-between'
                    }}>
                      <span>{mat.description}</span>
                      <span style={{ fontWeight: '600' }}>{mat.quantity} {mat.unit}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Exports */}
              {exportMaterials.length > 0 && (
                <div>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#991b1b',
                    marginBottom: '8px',
                    textTransform: 'uppercase'
                  }}>
                    {t.exports}:
                  </div>
                  {exportMaterials.map((mat, index) => (
                    <div key={index} style={{
                      padding: '8px 12px',
                      backgroundColor: '#fef2f2',
                      borderRadius: '6px',
                      marginBottom: '6px',
                      fontSize: '14px',
                      color: '#991b1b',
                      display: 'flex',
                      justifyContent: 'space-between'
                    }}>
                      <span>{mat.description}</span>
                      <span style={{ fontWeight: '600' }}>{mat.quantity} {mat.unit}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Dumpsters */}
              {dumpsters.length > 0 && (
                <div style={{ marginTop: '16px' }}>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#7c3aed',
                    marginBottom: '8px',
                    textTransform: 'uppercase'
                  }}>
                    🗑️ {t.dumpsters}:
                  </div>
                  {dumpsters.map((dump, index) => (
                    <div key={index} style={{
                      padding: '8px 12px',
                      backgroundColor: '#f5f3ff',
                      borderRadius: '6px',
                      marginBottom: '6px',
                      fontSize: '14px',
                      color: '#7c3aed',
                      display: 'flex',
                      justifyContent: 'space-between'
                    }}>
                      <span>{dump.dumpster_type}</span>
                      <span style={{ fontWeight: '600' }}>{dump.quantity}x {dump.size} Yard</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Notes Section */}
        {notes && notes.trim() && (
          <div style={{ borderBottom: '1px solid #e5e7eb' }}>
            <div style={{
              padding: '12px 20px',
              backgroundColor: '#f9fafb',
              borderBottom: '1px solid #e5e7eb',
              fontSize: '13px',
              fontWeight: '700',
              color: '#374151'
            }}>
              {t.notes}
            </div>
            <div style={{
              padding: '16px 20px',
              fontSize: '14px',
              color: '#374151',
              lineHeight: '1.6',
              whiteSpace: 'pre-wrap'
            }}>
              {notes}
            </div>
          </div>
        )}

        {/* Superintendent Signature Section */}
        <div style={{ padding: '20px' }}>
          <div style={{
            fontSize: '13px',
            fontWeight: '700',
            color: '#374151',
            marginBottom: '16px',
            textAlign: 'center'
          }}>
            {t.superintendentSignature}
          </div>

          {/* Signature Pad */}
          <div style={{ marginBottom: '12px' }}>
            <SignaturePad
              onSignatureChange={setSignatureBase64}
              height={180}
              language={language}
            />
          </div>

          {/* Signature Line */}
          <div style={{ textAlign: 'center', marginTop: '8px' }}>
            <div style={{
              borderTop: '1px solid #9ca3af',
              width: '80%',
              margin: '0 auto 8px',
              paddingTop: '8px',
              fontSize: '12px',
              color: '#6b7280'
            }}>
              {t.authorizedSignature}
            </div>
            <div style={{ fontSize: '12px', color: '#9ca3af' }}>
              {t.date}: {formattedDate}
            </div>
          </div>

          {/* Signature Required Message */}
          {!signatureBase64 && (
            <div style={{
              textAlign: 'center',
              marginTop: '12px',
              fontSize: '13px',
              color: '#dc2626',
              fontStyle: 'italic'
            }}>
              {t.signatureRequired}
            </div>
          )}
        </div>

        {/* Action Buttons - Inside scrolling area */}
        <div style={{
          padding: '20px',
          display: 'flex',
          gap: '12px',
          borderTop: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb'
        }}>
        <button
          type="button"
          onClick={onEdit}
          disabled={isSubmitting}
          style={{
            flex: 1,
            padding: '14px',
            backgroundColor: '#f3f4f6',
            color: '#374151',
            border: '2px solid #d1d5db',
            borderRadius: '10px',
            fontSize: '15px',
            fontWeight: '600',
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          <ArrowLeft size={18} />
          {t.editTicket}
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!signatureBase64 || isSubmitting}
          style={{
            flex: 2,
            padding: '14px',
            backgroundColor: (!signatureBase64 || isSubmitting) ? '#9ca3af' : '#22c55e',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            fontSize: '15px',
            fontWeight: '600',
            cursor: (!signatureBase64 || isSubmitting) ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          <Send size={18} />
          {isSubmitting
            ? (language === 'en' ? 'Submitting...' : 'Enviando...')
            : t.submitSignedTicket
          }
        </button>
        </div>
      </div>
    </div>
  );
};

export default TMTicketPreview;
